import { useState, useEffect } from 'react'
import {
  X,
  Shield,
  Download,
  CheckCircle2,
  Clock,
  FileText,
  User,
  Globe,
  Monitor,
  Smartphone,
  Tablet,
  Hash,
  Loader2,
  AlertCircle,
  ShieldCheck
} from 'lucide-react'
import { toast } from 'sonner'
import api from '../lib/api'
import type { AuditEvent, AuditCertificate } from '../lib/api'
import { useLocale } from '../contexts/LocaleContext'

interface AuditTrailModalProps {
  isOpen: boolean
  onClose: () => void
  proposalId: string
  proposalTitle: string
}

function maskIpAddress(ip: string | null, notAvailableText: string): string {
  if (!ip) return notAvailableText
  const parts = ip.split('.')
  if (parts.length === 4) {
    return `${parts[0]}.${parts[1]}.*.*`
  }
  // IPv6 or other - just show partial
  return ip.substring(0, Math.min(ip.length, 12)) + '...'
}

function parseDeviceInfo(userAgent: string | null, labels: { unknown: string; mobile: string; tablet: string; desktop: string }): { type: string; icon: React.ReactNode; label: string } {
  if (!userAgent) return { type: 'unknown', icon: <Monitor className="w-4 h-4" />, label: labels.unknown }

  const ua = userAgent.toLowerCase()
  if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
    return { type: 'mobile', icon: <Smartphone className="w-4 h-4" />, label: labels.mobile }
  }
  if (ua.includes('ipad') || ua.includes('tablet')) {
    return { type: 'tablet', icon: <Tablet className="w-4 h-4" />, label: labels.tablet }
  }
  return { type: 'desktop', icon: <Monitor className="w-4 h-4" />, label: labels.desktop }
}

function truncateHash(hash: string, notAvailableText: string, length: number = 12): string {
  if (!hash) return notAvailableText
  if (hash.length <= length) return hash
  return hash.substring(0, length) + '...'
}

function formatDateTime(isoString: string, locale: string, notAvailableText: string): string {
  if (!isoString) return notAvailableText
  try {
    const date = new Date(isoString)
    return date.toLocaleString(locale === 'he' ? 'he-IL' : 'en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return isoString
  }
}

function downloadCertificateAsJson(certificate: AuditCertificate, proposalTitle: string) {
  const blob = new Blob([JSON.stringify(certificate, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `audit-certificate-${proposalTitle.replace(/\s+/g, '-')}.json`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export default function AuditTrailModal({
  isOpen,
  onClose,
  proposalId,
  proposalTitle,
}: AuditTrailModalProps) {
  const { tx, locale } = useLocale()
  const [events, setEvents] = useState<AuditEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [downloadingCert, setDownloadingCert] = useState(false)

  const notAvailableText = tx('Not available', 'לא זמין')
  const deviceLabels = {
    unknown: tx('Unknown', 'לא ידוע'),
    mobile: tx('Mobile', 'נייד'),
    tablet: tx('Tablet', 'טאבלט'),
    desktop: tx('Desktop', 'מחשב')
  }

  useEffect(() => {
    if (isOpen && proposalId) {
      fetchAuditTrail()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, proposalId])

  const fetchAuditTrail = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.getAuditTrail(proposalId)
      setEvents(response.events || [])
    } catch (err) {
      const message = err instanceof Error ? err.message : tx('Error loading audit trail', 'שגיאה בטעינת היסטוריית הפעולות')
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadCertificate = async () => {
    setDownloadingCert(true)
    try {
      const response = await api.getAuditCertificate(proposalId)
      downloadCertificateAsJson(response.certificate, proposalTitle)
      toast.success(tx('Verification certificate downloaded successfully', 'תעודת האימות הורדה בהצלחה'))
    } catch (err) {
      const message = err instanceof Error ? err.message : tx('Error downloading verification certificate', 'שגיאה בהורדת תעודת האימות')
      toast.error(message)
    } finally {
      setDownloadingCert(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-700 to-slate-700 p-5 text-white shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold">{tx('Signature Audit Trail', 'היסטוריית חתימות')}</h2>
                <p className="text-sm text-white/80 mt-0.5 truncate max-w-[250px]">{proposalTitle}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-slate-500 animate-spin mb-3" />
              <p className="text-sm text-slate-500">{tx('Loading audit trail...', 'טוען היסטוריית פעולות...')}</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="w-8 h-8 text-rose-400 mb-3" />
              <p className="text-sm text-rose-600">{error}</p>
              <button
                onClick={fetchAuditTrail}
                className="mt-3 px-4 py-2 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                {tx('Try Again', 'נסה שוב')}
              </button>
            </div>
          ) : events.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Shield className="w-8 h-8 text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">{tx('No audit events for this proposal', 'אין אירועים להצעה זו')}</p>
              <p className="text-xs text-slate-400 mt-1">{tx('Events will be recorded when the proposal is signed', 'אירועים יתועדו כאשר ההצעה תיחתם')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Timeline */}
              {events.map((event, index) => {
                const device = parseDeviceInfo(event.user_agent, deviceLabels)
                return (
                  <div key={event.id} className="relative">
                    {/* Timeline connector */}
                    {index < events.length - 1 && (
                      <div className="absolute left-[19px] top-10 bottom-0 w-0.5 bg-slate-200" />
                    )}

                    <div className="flex gap-4">
                      {/* Timeline dot */}
                      <div className="shrink-0 mt-1">
                        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        </div>
                      </div>

                      {/* Event content */}
                      <div className="flex-1 bg-slate-50 rounded-xl p-4 border border-slate-200">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-md">
                            {tx('Digital Signature', 'חתימה דיגיטלית')}
                          </span>
                        </div>

                        {/* Timestamp */}
                        <div className="flex items-center gap-2 text-sm text-slate-700 mb-3">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">{formatDateTime(event.timestamp, locale, notAvailableText)}</span>
                        </div>

                        {/* Details grid */}
                        <div className="space-y-2">
                          {/* Signer */}
                          <div className="flex items-center gap-2 text-sm">
                            <User className="w-4 h-4 text-slate-400 shrink-0" />
                            <span className="text-slate-500">{tx('Signer:', 'חותם:')}</span>
                            <span className="font-medium text-slate-700">{event.signer_name}</span>
                          </div>

                          {/* Email */}
                          {event.signer_email && (
                            <div className="flex items-center gap-2 text-sm">
                              <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                              <span className="text-slate-500">{tx('Email:', 'דוא"ל:')}</span>
                              <span className="font-medium text-slate-700">{event.signer_email}</span>
                            </div>
                          )}

                          {/* IP Address */}
                          <div className="flex items-center gap-2 text-sm">
                            <Globe className="w-4 h-4 text-slate-400 shrink-0" />
                            <span className="text-slate-500">{tx('IP Address:', 'כתובת IP:')}</span>
                            <span className="font-mono text-xs bg-slate-200 px-2 py-0.5 rounded text-slate-600">
                              {maskIpAddress(event.ip_address, notAvailableText)}
                            </span>
                          </div>

                          {/* Device */}
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-slate-400 shrink-0">{device.icon}</span>
                            <span className="text-slate-500">{tx('Device:', 'מכשיר:')}</span>
                            <span className="font-medium text-slate-700">{device.label}</span>
                          </div>

                          {/* Document Hash */}
                          <div className="flex items-center gap-2 text-sm">
                            <Hash className="w-4 h-4 text-slate-400 shrink-0" />
                            <span className="text-slate-500">{tx('Document hash:', 'חתימת מסמך:')}</span>
                            <span
                              className="font-mono text-xs bg-slate-50 text-slate-700 px-2 py-0.5 rounded cursor-help"
                              title={event.document_hash}
                            >
                              {truncateHash(event.document_hash, notAvailableText)}
                            </span>
                          </div>

                          {/* Signature Hash */}
                          <div className="flex items-center gap-2 text-sm">
                            <Hash className="w-4 h-4 text-slate-400 shrink-0" />
                            <span className="text-slate-500">{tx('Signature hash:', 'חתימת חתימה:')}</span>
                            <span
                              className="font-mono text-xs bg-slate-50 text-slate-700 px-2 py-0.5 rounded cursor-help"
                              title={event.signature_data_hash}
                            >
                              {truncateHash(event.signature_data_hash, notAvailableText)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 bg-slate-50 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Shield className="w-4 h-4 text-slate-500" />
              <span>{tx('Secured with SHA-256', 'מאובטח עם SHA-256')}</span>
            </div>
            <button
              onClick={handleDownloadCertificate}
              disabled={downloadingCert || events.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {downloadingCert ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              <span>{tx('Download Certificate', 'הורד תעודה')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
